import React, { useState } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';

import Breadcrumbs from '@/components/Breadcrumbs';
import CardGrid from '@/components/CardGrid';
import HeroImage from '@/components/layout/HeroImage';
import Pagination from '@/components/Pagination';
import SearchBox from '@/components/SearchBox';
import SortOptions from '@/components/SortOptions';
import useIsMobile from '@/hooks/useIsMobile';
import usePagination from '@/hooks/usePagination';
import useStore from '@/hooks/useStore';
import { getServerSidePropsWithGlobalData } from '@/lib/getServerSideProps';
import { extractFragment } from '@/lib/GraphQLHelper';
import { withUrql } from '@/lib/urqlClient';
import { getQueryParam } from '@/lib/Utils';
import { ErrorPage } from '@/pages/_error';

import query from './query.public.graphql';

import styles from './styles.module.scss';

export interface Props {
  inputPage?: string;
}

const ShopCategory = ({ inputPage }: Props) => {
  const { exclusive } = useStore('userStore');
  const { availableBlackHawkIds } = useStore('cart');
  const [sortBy, setSortBy] = useState<sortOptions>('popular');
  const [isMobile] = useIsMobile(768);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, pageInvalid] = usePagination(inputPage || '1');
  const router = useRouter();
  const isGiftCards = router.query.category === 'gift-cards';

  const [{ data, error, fetching }] = useQuery<PublicCategoryProductQuery>({
    query,
    variables: {
      category: router.query.category,
      featuredSearch: `productCategories:${router.query.category}`,
      orderBy: ShopCategory.sortFilters[sortBy],
      exclusive,
      search: !isGiftCards ? `productCategories:${router.query.category} ${searchQuery}` : `${searchQuery}`,
      limit: ShopCategory.limit,
      offset: (page - 1) * ShopCategory.limit,
      type: isGiftCards ? 'giftCards' : 'eTickets',
      ids: isGiftCards ? availableBlackHawkIds : [],
    },
  });

  if (
    (!fetching && ((isGiftCards && !data?.productsGiftCards) || (!isGiftCards && !data?.productCountETickets))) ||
    pageInvalid
  ) {
    return <ErrorPage statusCode={404} err={error} />;
  }

  const catergoryEntry = extractFragment<ProductCategoryFragment>(data?.category);

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Online Shop', uri: '/shop' },
    { title: catergoryEntry?.title || '' },
  ];

  const products = isGiftCards ? data?.productsGiftCards : data?.productsETickets;
  const productCount = isGiftCards ? data?.productCountGiftCards : data?.productCountETickets;

  return (
    <div className={styles.offerCategory} data-testid="shopCategoryPage">
      <HeroImage image={catergoryEntry?.heroImage?.[0]?.url} />
      <div className={styles.inner}>
        <h1>{catergoryEntry?.title}</h1>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        {!isGiftCards && (
          <CardGrid
            title="Featured Items"
            cards={data?.featured as ETicketFragment[]}
            slider={isMobile}
            small={isMobile}
            whiteText={true}
          />
        )}
        {!!products?.length ? (
          <>
            <div className={styles.top}>
              <div className={styles.search}>
                <SearchBox onChange={setSearchQuery} />
                <span>Showing {products?.length} Products</span>
              </div>
              <div className={styles.sort}>
                <SortOptions callback={setSortBy} defaultValue={sortBy} options={ShopCategory.sortOptions} />
              </div>
            </div>
            <div className={styles.entries}>
              <CardGrid
                title={!isMobile ? `Browse ${catergoryEntry?.title} Offers` : ''}
                cards={data?.products as ETicketFragment[]}
                whiteText={!data?.featured?.length}
              />
            </div>
            <Pagination
              numItems={productCount?.length}
              pageLimit={ShopCategory.limit}
              currentPage={page}
              baseURL={`/shop/${router.query.category}`}
            />
          </>
        ) : (
          <h1 className={styles.noOffer}>No products currently available</h1>
        )}
      </div>
    </div>
  );
};

ShopCategory.sortOptions = [
  { title: 'Sort By', value: '', disabled: true },
  { title: 'Popular', value: 'popular' },
  { title: 'Newest', value: 'newest' },
];

ShopCategory.sortFilters = {
  popular: 'popularOffer DESC',
  newest: 'dateCreated DESC',
  ending: 'expiryDate DESC',
  recommended: 'lft',
} as Record<sortOptions, string | null>;

ShopCategory.limit = 12;

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) =>
  await getServerSidePropsWithGlobalData(ctx, async ({ nextCtx, stores, urqlClient }) => {
    const category = getQueryParam(nextCtx.query.category);
    const isGiftCards = category === 'gift-cards';
    const inputPage = Number.parseInt(getQueryParam(nextCtx.query.slug) || '1');

    await urqlClient
      .query<PublicCategoryProductQuery, PublicCategoryProductQueryVariables>(query, {
        category: category,
        featuredSearch: `productCategories:${category}`,
        orderBy: ShopCategory.sortFilters['popular'],
        exclusive: stores.userStore.exclusive,
        search: !isGiftCards ? `productCategories:${category} ${''}` : `${''}`,
        limit: ShopCategory.limit,
        offset: (inputPage - 1) * ShopCategory.limit,
        type: isGiftCards ? 'giftCards' : 'eTickets',
        ids: isGiftCards ? stores.cart?.availableBlackHawkIds.filter((f): f is string => !!f) : null,
      })
      .toPromise();

    return { props: { inputPage } };
  });

export default withUrql(ShopCategory);
